import * as z from "zod/v4";

import { ReactLazyLoading } from "@/containers/ReactLoading";

import type { FormPropsType } from "./types";

type ComponentBag = {
  list: React.ComponentType;
  new: React.ComponentType;
  edit: React.ComponentType;
  show: React.ComponentType;
  form: React.ComponentType<FormPropsType<any>>;
  formUtils?: () => Promise<{
    initialValues: (props: FormPropsType<any>) => any;
    validationSchema?: z.Schema;
  }>;
  export: React.ComponentType;
  audit?: React.ComponentType;
  offers: React.ComponentType;
  products: React.ComponentType;
};

export const COMPONENTS: TTDict<string, TTDict<string, Partial<ComponentBag>>> = {
  "sevenit-gmbh": {
    default: {
      list: ReactLazyLoading(() => import("@/customers/sevenit/CustomerContacts/support/List")),
      show: ReactLazyLoading(() => import("@/customers/sevenit/CustomerContacts/support/Show")),
      form: ReactLazyLoading(() => import("@/customers/sevenit/CustomerContacts/support/Form")),
      formUtils: async () => await import("@/customers/sevenit/CustomerContacts/support/Form/formUtils"),
    },
    service: {
      list: ReactLazyLoading(() => import("@/customers/sevenit/CustomerContacts/service/List")),
      form: ReactLazyLoading(() => import("@/customers/sevenit/CustomerContacts/support/Form")),
      formUtils: async () => await import("@/customers/sevenit/CustomerContacts/support/Form/formUtils"),
      show: ReactLazyLoading(() => import("@/customers/sevenit/CustomerContacts/service/Show")),
    },
    retention: {
      form: ReactLazyLoading(() => import("@/customers/sevenit/CustomerContacts/retention/Form")),
      formUtils: async () => await import("@/customers/sevenit/CustomerContacts/retention/Form/formUtils"),
      show: ReactLazyLoading(() => import("@/customers/sevenit/CustomerContacts/retention/Show")),
      list: ReactLazyLoading(() => import("@/customers/sevenit/CustomerContacts/retention/List")),
    },
  },

  "sk-dienstleistungen": {
    default: {
      list: ReactLazyLoading(() => import("@/customers/sk/List")),
      show: ReactLazyLoading(() => import("@/customers/sk/Show")),
      form: ReactLazyLoading(() => import("@/customers/sk/Form")),
    },
  },

  smr: {
    default: {
      list: ReactLazyLoading(() => import("@/customers/smr/CustomerContacts/List")),
      form: ReactLazyLoading(() => import("@/customers/smr/CustomerContacts/Form")),
      show: ReactLazyLoading(() => import("@/customers/smr/CustomerContacts/Show")),
    },
    "fini-resort-badenweiler": {
      list: ReactLazyLoading(() => import("@/customers/smr/CustomerContacts/fini-resort/List")),
      form: ReactLazyLoading(() => import("@/customers/smr/CustomerContacts/fini-resort/Form")),
      formUtils: async () => await import("@/customers/smr/CustomerContacts/fini-resort/Form/formUtils"),
      show: ReactLazyLoading(() => import("@/customers/smr/CustomerContacts/fini-resort/Show")),
    },
  },

  "lr-health-beauty": {
    service: {
      form: ReactLazyLoading(() => import("@/customers/lr-health-beauty/service/Tasks/Form")),
      formUtils: async () => await import("@/customers/lr-health-beauty/service/Tasks/Form/formUtils"),
      show: ReactLazyLoading(() => import("@/customers/lr-health-beauty/service/Tasks/Show")),
      list: ReactLazyLoading(() => import("@/customers/lr-health-beauty/service/Tasks/List")),
      products: ReactLazyLoading(() => import("./Offers/Products")),
    },
    "outbound-exit": {
      form: ReactLazyLoading(() => import("@/customers/lr-health-beauty/outbound-exit/Tasks/Form")),
      formUtils: async () => await import("@/customers/lr-health-beauty/outbound-exit/Tasks/Form/formUtils"),
      show: ReactLazyLoading(() => import("@/customers/lr-health-beauty/outbound-exit/Tasks/Show")),
      list: ReactLazyLoading(() => import("@/customers/lr-health-beauty/outbound-exit/Tasks/List")),
    },
    "lr-uk": {
      form: ReactLazyLoading(() => import("@/customers/lr-health-beauty/lr-uk/Tasks/Form")),
      show: ReactLazyLoading(() => import("@/customers/lr-health-beauty/lr-uk/Tasks/Show")),
      list: ReactLazyLoading(() => import("@/customers/lr-health-beauty/lr-uk/Tasks/List")),
    },
    "service-uk": {
      form: ReactLazyLoading(() => import("@/customers/lr-health-beauty/uk-service/Tasks/Form")),
      show: ReactLazyLoading(() => import("@/customers/lr-health-beauty/uk-service/Tasks/Show")),
      list: ReactLazyLoading(() => import("@/customers/lr-health-beauty/uk-service/Tasks/List")),
    },
    vertrieb: {
      form: ReactLazyLoading(() => import("@/customers/lr-health-beauty/vertrieb/Tasks/Form")),
      formUtils: async () => await import("@/customers/lr-health-beauty/vertrieb/Tasks/Form/formUtils"),
      show: ReactLazyLoading(() => import("@/customers/lr-health-beauty/vertrieb/Tasks/Show")),
      list: ReactLazyLoading(() => import("@/customers/lr-health-beauty/vertrieb/Tasks/List")),
    },
  },

  "lr-france": {
    frankreich: {
      form: ReactLazyLoading(() => import("@/customers/lr-france/CustomerContacts/frankreich/Form")),
      formUtils: async () => await import("@/customers/lr-france/CustomerContacts/frankreich/Form/formUtils"),
      show: ReactLazyLoading(() => import("@/customers/lr-france/CustomerContacts/frankreich/Show")),
      list: ReactLazyLoading(() => import("@/customers/lr-france/CustomerContacts/frankreich/List")),
    },
    service: {
      form: ReactLazyLoading(() => import("@/customers/lr-france/CustomerContacts/service/Form")),
      formUtils: async () => await import("@/customers/lr-france/CustomerContacts/service/Form/formUtils"),
      show: ReactLazyLoading(() => import("@/customers/lr-france/CustomerContacts/service/Show")),
      list: ReactLazyLoading(() => import("@/customers/lr-france/CustomerContacts/service/List")),
    },
  },

  proxytel: {
    vertrieb: {
      form: ReactLazyLoading(() => import("@/customers/proxytel/vertrieb/Tasks/Form")),
      formUtils: async () => await import("@/customers/proxytel/vertrieb/Tasks/Form/formUtils"),
      show: ReactLazyLoading(() => import("@/customers/proxytel/vertrieb/Tasks/Show")),
      list: ReactLazyLoading(() => import("@/customers/proxytel/vertrieb/Tasks/List")),
    },
  },

  "mercedes-benz": {
    "van-bestandsfahrzeuge": {
      form: ReactLazyLoading(() => import("@/customers/mercedes-benz/Tasks/van-bestandsfahrzeuge/Form")),
      formUtils: async () => await import("@/customers/mercedes-benz/Tasks/van-bestandsfahrzeuge/Form/formUtils"),
      show: ReactLazyLoading(() => import("@/customers/mercedes-benz/Tasks/van-bestandsfahrzeuge/Show")),
      list: ReactLazyLoading(() => import("@/customers/mercedes-benz/Tasks/van-bestandsfahrzeuge/List")),
    },
    "paritaetischer-wohlfahrtsverband": {
      form: ReactLazyLoading(() => import("@/customers/mercedes-benz/Tasks/default/Form")),
      formUtils: async () => await import("@/customers/mercedes-benz/Tasks/default/Form/formUtils"),
      show: ReactLazyLoading(() => import("@/customers/mercedes-benz/Tasks/default/Show")),
      list: ReactLazyLoading(() => import("@/customers/mercedes-benz/Tasks/default/List")),
    },
    selbach: {
      form: ReactLazyLoading(() => import("@/customers/mercedes-benz/Tasks/default/Form")),
      formUtils: async () => await import("@/customers/mercedes-benz/Tasks/default/Form/formUtils"),
      show: ReactLazyLoading(() => import("@/customers/mercedes-benz/Tasks/default/Show")),
      list: ReactLazyLoading(() => import("@/customers/mercedes-benz/Tasks/default/List")),
    },
    "transporter-rheinland": {
      form: ReactLazyLoading(() => import("@/customers/mercedes-benz/Tasks/default/Form")),
      formUtils: async () => await import("@/customers/mercedes-benz/Tasks/default/Form/formUtils"),
      show: ReactLazyLoading(() => import("@/customers/mercedes-benz/Tasks/default/Show")),
      list: ReactLazyLoading(() => import("@/customers/mercedes-benz/Tasks/default/List")),
    },
  },

  zimmermann: {
    default: {
      form: ReactLazyLoading(() => import("@/customers/zimmermann/tasks/Form")),
      show: ReactLazyLoading(() => import("@/customers/zimmermann/tasks/Show")),
      list: ReactLazyLoading(() => import("@/customers/zimmermann/tasks/List")),
    },
  },

  termitel: {
    vertrieb: {
      form: ReactLazyLoading(() => import("@/customers/termitel/Vertrieb/Tasks/Form")),
      formUtils: async () => await import("@/customers/termitel/Vertrieb/Tasks/Form/formUtils"),
      show: ReactLazyLoading(() => import("@/customers/termitel/Vertrieb/Tasks/Show")),
      list: ReactLazyLoading(() => import("@/customers/termitel/Vertrieb/Tasks/List")),
    },
  },

  demo: {
    default: {
      form: ReactLazyLoading(() => import("@/customers/demo/Tasks/Form")),
      formUtils: async () => await import("@/customers/demo/Tasks/Form/formUtils"),
      show: ReactLazyLoading(() => import("@/customers/demo/Tasks/Show")),
      list: ReactLazyLoading(() => import("@/customers/demo/Tasks/List")),
    },
  },

  "allianz-vertretung-claus-buerger": {
    default: {
      form: ReactLazyLoading(() => import("@/customers/allianz-vertretung-claus-buerger/Tasks/Form")),
      formUtils: async () => await import("@/customers/allianz-vertretung-claus-buerger/Tasks/Form/formUtils"),
      show: ReactLazyLoading(() => import("@/customers/allianz-vertretung-claus-buerger/Tasks/Show")),
      list: ReactLazyLoading(() => import("@/customers/allianz-vertretung-claus-buerger/Tasks/List")),
    },
  },

  quantiquest: {
    siegel: {
      form: ReactLazyLoading(() => import("@/customers/quantiquest/Tasks/siegel/Form")),
      formUtils: async () => await import("@/customers/quantiquest/Tasks/siegel/Form/formUtils"),
      show: ReactLazyLoading(() => import("@/customers/quantiquest/Tasks/siegel/Show")),
      list: ReactLazyLoading(() => import("@/customers/quantiquest/Tasks/siegel/List")),
    },
    fahrschulen: {
      form: ReactLazyLoading(() => import("@/customers/quantiquest/Tasks/default/Form")),
      formUtils: async () => await import("@/customers/quantiquest/Tasks/default/Form/formUtils"),
      show: ReactLazyLoading(() => import("@/customers/quantiquest/Tasks/default/Show")),
      list: ReactLazyLoading(() => import("@/customers/quantiquest/Tasks/default/List")),
    },
    "top-anwalt": {
      form: ReactLazyLoading(() => import("@/customers/quantiquest/Tasks/default/Form")),
      formUtils: async () => await import("@/customers/quantiquest/Tasks/default/Form/formUtils"),
      show: ReactLazyLoading(() => import("@/customers/quantiquest/Tasks/default/Show")),
      list: ReactLazyLoading(() => import("@/customers/quantiquest/Tasks/default/List")),
    },
    default: {
      form: ReactLazyLoading(() => import("@/customers/quantiquest/Tasks/default/Form")),
      formUtils: async () => await import("@/customers/quantiquest/Tasks/default/Form/formUtils"),
      show: ReactLazyLoading(() => import("@/customers/quantiquest/Tasks/default/Show")),
      list: ReactLazyLoading(() => import("@/customers/quantiquest/Tasks/default/List")),
    },
  },

  brinkmann: {
    default: {
      form: ReactLazyLoading(() => import("@/customers/brinkmann/Tasks/Form")),
      formUtils: async () => await import("@/customers/brinkmann/Tasks/Form/formUtils"),
      show: ReactLazyLoading(() => import("@/customers/brinkmann/Tasks/Show")),
      list: ReactLazyLoading(() => import("@/customers/brinkmann/Tasks/List")),
    },
  },

  "sug-ag": {
    "vertrieb-offensive-084": {
      list: ReactLazyLoading(() => import("@/customers/sug/Tasks/vertrieb-offensive-084/List")),
    },
    default: {
      form: ReactLazyLoading(() => import("@/customers/sug/Tasks/Form")),
      formUtils: async () => await import("@/customers/sug/Tasks/Form/formUtils"),
      show: ReactLazyLoading(() => import("@/customers/sug/Tasks/Show")),
    },
  },

  "sug-gmbh": {
    default: {
      form: ReactLazyLoading(() => import("@/customers/sug/Tasks/Form")),
      formUtils: async () => await import("@/customers/sug/Tasks/Form/formUtils"),
      show: ReactLazyLoading(() => import("@/customers/sug/Tasks/Show")),
    },
  },

  cityclean: {
    kundenrueckgewinnung: {
      form: ReactLazyLoading(() => import("@/customers/cityclean/kundenrueckgewinnung/Tasks/Form")),
      formUtils: async () => await import("@/customers/cityclean/kundenrueckgewinnung/Tasks/Form/formUtils"),
      show: ReactLazyLoading(() => import("@/customers/cityclean/kundenrueckgewinnung/Tasks/Show")),
    },
    saisonkunden: {
      form: ReactLazyLoading(() => import("@/customers/cityclean/saisonkunden/Tasks/Form")),
      formUtils: async () => await import("@/customers/cityclean/saisonkunden/Tasks/Form/formUtils"),
      show: ReactLazyLoading(() => import("@/customers/cityclean/saisonkunden/Tasks/Show")),
    },
    neugruender: {
      form: ReactLazyLoading(() => import("@/customers/cityclean/neugruender/Tasks/Form")),
      formUtils: async () => await import("@/customers/cityclean/neugruender/Tasks/Form/formUtils"),
      show: ReactLazyLoading(() => import("@/customers/cityclean/neugruender/Tasks/Show")),
      list: ReactLazyLoading(() => import("@/customers/cityclean/neugruender/Tasks/List")),
    },
  },

  "wuestenrot-sascha-maurer": {
    default: {
      form: ReactLazyLoading(() => import("@/customers/wuestenrot-sascha-maurer/Tasks/Form")),
      formUtils: async () => await import("@/customers/wuestenrot-sascha-maurer/Tasks/Form/formUtils"),
      show: ReactLazyLoading(() => import("@/customers/wuestenrot-sascha-maurer/Tasks/Show")),
      list: ReactLazyLoading(() => import("@/customers/wuestenrot-sascha-maurer/Tasks/List")),
    },
  },

  "infinity-media": {
    default: {
      form: ReactLazyLoading(() => import("@/customers/infinity-media/Tasks/Form")),
      formUtils: async () => await import("@/customers/infinity-media/Tasks/Form/formUtils"),
      show: ReactLazyLoading(() => import("@/customers/infinity-media/Tasks/Show")),
      list: ReactLazyLoading(() => import("@/customers/infinity-media/Tasks/List")),
    },
  },

  "gju-demo": {
    demo: {
      form: ReactLazyLoading(() => import("@/customers/gju-demo/Tasks/Form")),
      formUtils: async () => await import("@/customers/gju-demo/Tasks/Form/formUtils"),
      show: ReactLazyLoading(() => import("@/customers/gju-demo/Tasks/Show")),
      list: ReactLazyLoading(() => import("@/customers/gju-demo/Tasks/List")),
    },
  },
};

export const DEFAULT: ComponentBag = {
  list: ReactLazyLoading(() => import("./List")),
  new: ReactLazyLoading(() => import("./New")),
  edit: ReactLazyLoading(() => import("./Edit")),
  show: ReactLazyLoading(() => import("./Show")),
  form: ReactLazyLoading(() => import("./Form")),
  export: ReactLazyLoading(() => import("./Export")),
  offers: ReactLazyLoading(() => import("./Offers")),
  products: ReactLazyLoading(() => import("./Offers/Products")),
};

export const hasComponent = (session: SessionInterface, name: keyof ComponentBag): boolean =>
  !!COMPONENTS[session.currentCustomer.identifier]?.[session.currentProject.identifier]?.[name] ||
  !!COMPONENTS[session.currentCustomer.identifier]?.default?.[name] ||
  !!DEFAULT[name];

export const getFormUtils = (session: SessionInterface) =>
  COMPONENTS[session.currentCustomer.identifier]?.[session.currentProject.identifier]?.formUtils ||
  COMPONENTS[session.currentCustomer.identifier]?.default?.formUtils ||
  DEFAULT.formUtils;
